import { Slider } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { MODE_LABEL, formatMinutes } from './estimate';
import { DISTANCE_STEPS, TRAVEL_MODES, type TravelMode } from './walkshed';

/**
 * The travel controls the sidebar and the phone's map overlay share: the
 * two-or-three-way segmented toggles and the minutes slider.
 */

const Group = styled('div')({
  display: 'flex',
  width: '100%',
  padding: 3,
  gap: 2,
  borderRadius: 999,
  border: '1px solid rgba(243,238,231,0.45)',
  boxSizing: 'border-box',
});

const Segment = styled('button')({
  appearance: 'none',
  flex: 1,
  minWidth: 0,
  border: 'none',
  borderRadius: 999,
  padding: '6px 8px',
  background: 'transparent',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  fontSize: 12.5,
  fontWeight: 700,
  lineHeight: 1.15,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  transition: 'background-color 160ms ease, color 160ms ease',
  '&[aria-pressed="true"]': { background: BRAND.parchment, color: BRAND.mocha },
  '@media (hover: hover)': {
    '&:not(:disabled):not([aria-pressed="true"]):hover': { background: 'rgba(243,238,231,0.14)' },
  },
  '&:disabled': { opacity: 0.38, cursor: 'default' },
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 1 },
});

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  /** Said to a screen reader and on hover, when the label alone is not enough. */
  title?: string;
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <Group role="group" aria-label={label}>
      {options.map((o) => (
        <Segment
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          disabled={disabled || o.disabled}
          title={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </Segment>
      ))}
    </Group>
  );
}

export function ModeToggle({
  mode,
  onChange,
  disabled,
}: {
  mode: TravelMode;
  onChange: (mode: TravelMode) => void;
  disabled?: boolean;
}) {
  return (
    <Segmented
      label="Travel by"
      value={mode}
      disabled={disabled}
      options={TRAVEL_MODES.map((m) => ({ value: m, label: MODE_LABEL[m] }))}
      onChange={onChange}
    />
  );
}

// --- the slider ------------------------------------------------------------------

/**
 * One stop per step, and one more past 90 for no limit, so the slider alone can
 * take the filter off: dragging to the end is "any distance".
 */
const ANY = DISTANCE_STEPS.length;
const MARKED = new Set([15, 30, 60, 90]);
const MARKS = DISTANCE_STEPS.map((m, i) => ({ value: i, label: MARKED.has(m) ? String(m) : undefined }))
  .filter((m) => m.label)
  .concat({ value: ANY, label: 'Any' });

const indexOf = (minutes: number | null) =>
  minutes == null ? ANY : Math.max(0, DISTANCE_STEPS.findIndex((m) => m >= minutes));
const minutesAt = (i: number): number | null => (i >= ANY ? null : DISTANCE_STEPS[i]);

const StyledSlider = styled(Slider)({
  color: BRAND.parchment,
  height: 3,
  padding: '10px 0',
  '& .MuiSlider-rail': { opacity: 0.3 },
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    boxShadow: 'none',
    '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(243,238,231,0.16)' },
    '&.Mui-active': { boxShadow: '0 0 0 9px rgba(243,238,231,0.16)' },
  },
  '& .MuiSlider-mark': { width: 2, height: 6, borderRadius: 1, backgroundColor: 'rgba(243,238,231,0.5)' },
  '& .MuiSlider-markActive': { backgroundColor: BRAND.mocha },
  '& .MuiSlider-markLabel': {
    top: 26,
    fontFamily: FONTS.body,
    fontSize: 10.5,
    fontWeight: 700,
    color: 'rgba(243,238,231,0.6)',
  },
  '& .MuiSlider-markLabelActive': { color: 'rgba(243,238,231,0.85)' },
  '&.Mui-disabled': { color: 'rgba(243,238,231,0.35)' },
});

export function DistanceSlider({
  minutes,
  onChange,
  disabled,
}: {
  minutes: number | null;
  onChange: (minutes: number | null) => void;
  disabled?: boolean;
}) {
  return (
    <StyledSlider
      aria-label="Travel time"
      value={indexOf(minutes)}
      min={0}
      max={ANY}
      step={1}
      marks={MARKS}
      disabled={disabled}
      getAriaValueText={(i) => {
        const m = minutesAt(i);
        return m == null ? 'Any distance' : `Within ${formatMinutes(m)}`;
      }}
      onChange={(_, i) => {
        const next = minutesAt(i as number);
        if (next !== minutes) onChange(next);
      }}
    />
  );
}
